/**
 * useFeedback Hook
 *
 * Hook for submitting feedback and retrieving feedback submissions.
 * Handles context collection (page URL, browser info) automatically.
 * Uses Supabase for backend storage.
 */

import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useToast } from '../contexts/ToastContext';
import type { FeedbackFormData, FeedbackSubmission, FeedbackFilters, UpdateFeedbackInput } from '../types/feedback';

export const useFeedback = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Auto-collect context information for debugging
   */
  const collectContext = useCallback(() => {
    return {
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      app_version: '1.0.0-alpha', // TODO: Pull from package.json or env
    };
  }, []);

  /**
   * Submit new feedback
   */
  const submitFeedback = useCallback(
    async (formData: FeedbackFormData): Promise<boolean> => {
      if (!user) {
        showError('You must be logged in to submit feedback');
        return false;
      }

      setIsSubmitting(true);

      try {
        const context = collectContext();

        const { data, error } = await supabase
          .from('feedback_submissions')
          .insert({
            user_id: user.uid,
            user_email: user.email || 'unknown@email.com',
            type: formData.type,
            title: formData.title,
            description: formData.description,
            page_url: context.page_url,
            user_agent: context.user_agent,
            app_version: context.app_version,
            status: 'new',
            priority: 'medium',
          })
          .select()
          .single();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Feedback submitted successfully:', data);
        success('Thank you! Your feedback has been submitted.');
        return true;
      } catch (err) {
        console.error('Error submitting feedback:', err);
        showError('Failed to submit feedback. Please check your connection.');
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [user, collectContext, success, showError]
  );

  /**
   * Get all feedback submissions (admin only)
   */
  const getAllFeedback = useCallback(
    async (filters?: FeedbackFilters): Promise<FeedbackSubmission[]> => {
      setIsLoading(true);

      try {
        let query = supabase
          .from('feedback_submissions')
          .select('*')
          .order('created_at', { ascending: false });

        // Apply filters (only if not 'all')
        if (filters?.type && filters.type !== 'all') {
          query = query.eq('type', filters.type);
        }
        if (filters?.status && filters.status !== 'all') {
          query = query.eq('status', filters.status);
        }
        if (filters?.priority && filters.priority !== 'all') {
          query = query.eq('priority', filters.priority);
        }
        if (filters?.search) {
          // Search in title and description
          query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
        }
        if (filters?.startDate) {
          query = query.gte('created_at', filters.startDate.toISOString());
        }
        if (filters?.endDate) {
          query = query.lte('created_at', filters.endDate.toISOString());
        }

        const { data, error } = await query;

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Feedback fetched successfully:', data?.length, 'items');
        return data || [];
      } catch (err) {
        console.error('Error fetching feedback:', err);
        showError('Failed to load feedback');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [showError]
  );

  /**
   * Get feedback by ID (admin only)
   */
  const getFeedbackById = useCallback(
    async (id: string): Promise<FeedbackSubmission | null> => {
      try {
        const { data, error } = await supabase
          .from('feedback_submissions')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Feedback fetched successfully:', data);
        return data;
      } catch (err) {
        console.error('Error fetching feedback:', err);
        showError('Failed to load feedback details');
        return null;
      }
    },
    [showError]
  );

  /**
   * Get current user's feedback submissions
   */
  const getMyFeedback = useCallback(async (): Promise<FeedbackSubmission[]> => {
    if (!user) return [];

    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from('feedback_submissions')
        .select('*')
        .eq('user_id', user.uid)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('My feedback fetched successfully:', data?.length, 'items');
      return data || [];
    } catch (err) {
      console.error('Error fetching my feedback:', err);
      showError('Failed to load your feedback');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [user, showError]);

  /**
   * Update feedback (admin only)
   */
  const updateFeedback = useCallback(
    async (id: string, updates: UpdateFeedbackInput): Promise<boolean> => {
      try {
        const { data, error } = await supabase
          .from('feedback_submissions')
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id)
          .select()
          .single();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Feedback updated successfully:', data);
        success('Feedback updated successfully');
        return true;
      } catch (err) {
        console.error('Error updating feedback:', err);
        showError('Failed to update feedback');
        return false;
      }
    },
    [success, showError]
  );

  /**
   * Delete feedback (admin only)
   */
  const deleteFeedback = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from('feedback_submissions')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Feedback deleted successfully:', id);
        success('Feedback deleted successfully');
        return true;
      } catch (err) {
        console.error('Error deleting feedback:', err);
        showError('Failed to delete feedback');
        return false;
      }
    },
    [success, showError]
  );

  return {
    submitFeedback,
    getAllFeedback,
    getFeedbackById,
    getMyFeedback,
    updateFeedback,
    deleteFeedback,
    isSubmitting,
    isLoading,
  };
};
